export type SiteKey = 'photocouture' | 'asgerhummelgaard' | 'crds';

export type SiteConfig = {
    key: SiteKey;
    name: string;
    tagline: string;
    hostnames: string[];
};

const SITE_STORAGE_KEY = 'currentSiteKey';
export const SITE_CHANGE_EVENT = 'photocouture:site-change';
export const SITE_BRANDING_UPDATED_EVENT = 'photocouture:site-branding-updated';

export const SITE_CONFIGS: Record<SiteKey, SiteConfig> = {
    photocouture: {
        key: 'photocouture',
        name: 'Photocouture',
        tagline: 'Art photography by Finn Albæk',
        hostnames: ['a.photocouture.dk'],
    },
    asgerhummelgaard: {
        key: 'asgerhummelgaard',
        name: 'Asger Hummelgaard',
        tagline: 'Photography',
        hostnames: ['ab-hommelgaard.dk'],
    },
    crds: {
        key: 'crds',
        name: 'CRDS',
        tagline: 'Photography',
        hostnames: ['c.crds.dk'],
    },
};

export const SITE_OPTIONS = Object.values(SITE_CONFIGS);

export const normalizeSiteKey = (value: string | null | undefined): SiteKey => {
    if (value === 'asgerhummelgaard') return 'asgerhummelgaard';
    if (value === 'crds') return 'crds';
    return 'photocouture';
};

export const detectSiteKey = (): SiteKey => {
    const hostname = window.location.hostname.toLowerCase().replace(/^www\./, '');
    const match = SITE_OPTIONS.find((site) => site.hostnames.includes(hostname));
    return match?.key || 'photocouture';
};

export const getCurrentSiteKey = (): SiteKey => {
    const stored = window.localStorage.getItem(SITE_STORAGE_KEY);
    return stored && stored in SITE_CONFIGS ? normalizeSiteKey(stored) : detectSiteKey();
};

export const getCurrentSiteConfig = () => SITE_CONFIGS[getCurrentSiteKey()];

export const setCurrentSiteKey = (siteKey: string) => {
    const normalizedSiteKey = normalizeSiteKey(siteKey);
    window.localStorage.setItem(SITE_STORAGE_KEY, normalizedSiteKey);
    window.dispatchEvent(new CustomEvent(SITE_CHANGE_EVENT, { detail: normalizedSiteKey }));
};

export const notifySiteBrandingUpdated = (siteKey?: string) => {
    const normalizedSiteKey = normalizeSiteKey(siteKey || getCurrentSiteKey());
    window.dispatchEvent(new CustomEvent(SITE_BRANDING_UPDATED_EVENT, { detail: normalizedSiteKey }));
};

export const subscribeToSiteChange = (callback: (siteKey: SiteKey) => void) => {
    const handler = (event: Event) => {
        const detail = event instanceof CustomEvent ? event.detail : null;
        callback(normalizeSiteKey(detail || getCurrentSiteKey()));
    };
    window.addEventListener(SITE_CHANGE_EVENT, handler);
    return () => window.removeEventListener(SITE_CHANGE_EVENT, handler);
};

export const subscribeToSiteBrandingUpdate = (callback: (siteKey: SiteKey) => void) => {
    const handler = (event: Event) => {
        const detail = event instanceof CustomEvent ? event.detail : null;
        callback(normalizeSiteKey(detail || getCurrentSiteKey()));
    };
    window.addEventListener(SITE_BRANDING_UPDATED_EVENT, handler);
    return () => window.removeEventListener(SITE_BRANDING_UPDATED_EVENT, handler);
};